/**
 * Shared CSV + county-letterhead PDF exports for list reports.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { authUrl, execute } from "@/api";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

export function absUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u) || String(u).startsWith("data:") || String(u).startsWith("blob:")) return u;
  try {
    return new URL(u, authUrl.endsWith("/") ? authUrl : `${authUrl}/`).href;
  } catch (_) {
    return u;
  }
}

export async function loadCountyLetterhead() {
  try {
    const data = new FormData();
    data.append("function", "getCompanyInfo");
    const res = await execute(data, authUrl);
    if (res?.data?.success && res.data.data?.companyInfo) {
      const c = res.data.data.companyInfo;
      return {
        ...c,
        logoPrimaryUrl: absUrl(c.logoPrimaryUrl || c.logoPrimary),
        logoSecondaryUrl: absUrl(c.logoSecondaryUrl || c.logoSecondary),
      };
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * @param {string[]} headers
 * @param {Array<Array|Object>} rows - array of arrays, or objects with header keys
 * @param {string} filename
 */
export function downloadCsv(headers, rows, filename = "report.csv") {
  const lines = [headers.map(csvEscape).join(",")];
  (rows || []).forEach((r) => {
    if (Array.isArray(r)) {
      lines.push(r.map(csvEscape).join(","));
    } else {
      lines.push(headers.map((h) => csvEscape(r[h])).join(","));
    }
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Landscape county letterhead PDF table.
 * @param {object} opts
 */
export async function downloadCountyTablePdf(opts = {}) {
  const {
    title = "REPORT",
    subtitle = "",
    periodLabel = "",
    headers = [],
    rows = [],
    filename = `report_${Date.now()}.pdf`,
    orientation = "landscape",
    letterhead = null,
    summaryRows = null,
  } = opts;

  let lh = letterhead;
  if (!lh) lh = await loadCountyLetterhead();
  const logoDataUrl = await loadCountyLogoDataUrl(lh);

  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  let startY = addCountyLetterhead(doc, {
    title,
    subtitle,
    periodLabel: periodLabel || new Date().toLocaleString("en-KE"),
    logoDataUrl,
    letterhead: lh,
  });

  if (summaryRows && summaryRows.length) {
    autoTable(doc, {
      startY,
      head: [["Metric", "Value"]],
      body: summaryRows,
      styles: { fontSize: 8, cellPadding: 1.4 },
      headStyles: { fillColor: [12, 74, 110], textColor: 255, fontStyle: "bold" },
      margin: { left: 14, right: 14 },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 } },
    });
    startY = (doc.lastAutoTable && doc.lastAutoTable.finalY) + 6 || startY + 20;
  }

  const body = (rows || []).map((r, i) => {
    if (Array.isArray(r)) {
      return [String(i + 1), ...r.map((c) => (c == null ? "" : String(c)))];
    }
    return [String(i + 1), ...headers.map((h) => (r[h] == null ? "" : String(r[h])))];
  });

  autoTable(doc, {
    startY,
    head: [["#", ...headers]],
    body,
    styles: { fontSize: 7, cellPadding: 1.3, overflow: "linebreak" },
    headStyles: {
      fillColor: [12, 74, 110],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 7,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 12, right: 12 },
  });

  const pageCount = doc.internal.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const footer =
    (lh && lh.reportFooter) || "Official HRMIS document — County Government of Homa Bay";
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(String(footer), 14, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: "right" });
  }

  doc.save(filename);
  return doc;
}

export function periodLabelFromFilters(filters = {}) {
  const a = filters.startDate || filters.date_from || "";
  const b = filters.endDate || filters.date_to || "";
  if (a && b) return `${a} to ${b}`;
  if (a) return `From ${a}`;
  if (b) return `Until ${b}`;
  return "";
}
